import roundTripDocument from "./work-record-round-trip.json";
import type {CapturedTrim} from '@artemis-group/aiw-contracts';

export type HarnessUser = { id: string; name: string; device: string; surface: "mobile" | "aiw" | "installer_pwa" };
export type HarnessPoint = { x: number; y: number };
export type HarnessWall = { id: string; a: HarnessPoint; b: HarnessPoint; [key: string]: unknown };
export type HarnessMeasure = { id: string; axis: "x" | "y"; label: string; lengthMM: number | null; pxRun: number; a?: HarnessPoint; b?: HarnessPoint };
export type HarnessDoorway = { id: string; widthMM: number; transition?: string | null; [key: string]: unknown };
export type HarnessTrim = CapturedTrim & {
  id: string; type: string; lengthMM: number; polyline: HarnessPoint[]; at?: HarnessPoint | null;
  purchaseSKU?: string; stockLengthMM?: number; breakKind?: "straight" | "natural"; label?: string;
  [key: string]: unknown;
};
export type HarnessArea = {
  id: string; name: string; boundary: HarnessPoint[]; measures: HarnessMeasure[];
  doorways: HarnessDoorway[]; trims: HarnessTrim[];
  newFloor?: { product?: { productID: string; name?: string; type?: string | null; subType?: string | null; supplier?: string | null } };
  [key: string]: unknown;
};
export type HarnessFloor = {
  trimRuns?: HarnessTrim[];
  wallDoors?: HarnessDoorway[];
  id: string; name: string; areas: HarnessArea[]; walls?: HarnessWall[];
  registers: Array<{ kind: string; value: string; exceptions: unknown[] }>;
  plankLaserRows?: Array<{ id: string; label: string; readingsMM: number[] }>;
  plankCutSiteAllowsPrecut?: boolean;
  plankCutPlankLengthMM?: number;
  [key: string]: unknown;
};
export type HarnessAssessment = {
  jobRef: string; address?: string | null; floors: HarnessFloor[]; siteNote: string;
  [key: string]: unknown;
};
export type HarnessDocument = {
  schema: "artemis.work-record/1";
  assessment: HarnessAssessment;
  installer: Record<string, unknown> | null;
  site_details: Record<string, unknown>;
  assets: Record<string, unknown>;
  quantities: Record<string, unknown>;
};
export type HarnessRevision = {
  id: string;
  work_id: string;
  owner_user_id: string;
  owner_name: string;
  save_number: number;
  saved_at: string;
  device_name: string;
  surface: HarnessUser["surface"];
  idempotency_key: string;
  source_revision_id: string | null;
  source_owner_name: string | null;
  source_save_number: number | null;
  document: HarnessDocument;
};
export type HarnessWork = {
  id: string;
  stage: "opportunity" | "job";
  opportunity_ref: string;
  job_ref: string | null;
  title: string;
};

export const HARNESS_USERS: HarnessUser[] = [
  { id: "20000000-0000-4000-8000-000000000001", name: "Jacky", device: "iPad Pro", surface: "mobile" },
  { id: "20000000-0000-4000-8000-000000000002", name: "Estimator", device: "AIW browser", surface: "aiw" },
  { id: "20000000-0000-4000-8000-000000000003", name: "Installer", device: "Installer PWA", surface: "installer_pwa" },
];

const WORK_ID = "10000000-0000-4000-8000-000000000001";
let work: HarnessWork;
let revisions: HarnessRevision[];
let clock = 0;

function document(marker: number, note: string): HarnessDocument {
  const next = clone(roundTripDocument) as HarnessDocument;
  next.assessment.siteNote = note;
  const corner = next.assessment.floors[0]?.areas[0]?.boundary[2];
  if (corner) corner.x = 60 + 480 * marker / 100;
  return next;
}

function stamp(offsetMinutes: number): string {
  return new Date(Date.UTC(2026, 8, 19, 4, offsetMinutes)).toISOString();
}

function seed(): void {
  work = { id: WORK_ID, stage: "opportunity", opportunity_ref: "O-00000042", job_ref: null, title: "18 Example Street" };
  revisions = [
    {
      id: "30000000-0000-4000-8000-000000000001", work_id: WORK_ID,
      owner_user_id: HARNESS_USERS[0]!.id, owner_name: "Jacky", save_number: 1,
      saved_at: stamp(0), device_name: "iPad Pro", surface: "mobile", idempotency_key: "seed-jacky-1",
      source_revision_id: null, source_owner_name: null, source_save_number: null,
      document: document(24, "Site visit: wall scale captured; exact install check pending."),
    },
    {
      id: "30000000-0000-4000-8000-000000000002", work_id: WORK_ID,
      owner_user_id: HARNESS_USERS[0]!.id, owner_name: "Jacky", save_number: 2,
      saved_at: stamp(25), device_name: "iPad Pro", surface: "mobile", idempotency_key: "seed-jacky-2",
      source_revision_id: null, source_owner_name: null, source_save_number: null,
      document: document(42, "Door transition added. Door depth remains assumed at 110 mm."),
    },
    {
      id: "40000000-0000-4000-8000-000000000001", work_id: WORK_ID,
      owner_user_id: HARNESS_USERS[2]!.id, owner_name: "Installer", save_number: 1,
      saved_at: stamp(45), device_name: "Installer PWA", surface: "installer_pwa", idempotency_key: "seed-installer-1",
      source_revision_id: null, source_owner_name: null, source_save_number: null,
      document: document(66, "Install check opened; exact wall-to-wall still required."),
    },
  ];
  clock = 60;
}
seed();

function clone<T>(value: T): T {
  return structuredClone(value);
}

export function resetHarnessWork(): { work: HarnessWork; revisions: HarnessRevision[]; users: HarnessUser[] } {
  seed();
  return harnessSnapshot();
}

export function harnessSnapshot(ownerUserId?: string): { work: HarnessWork; revisions: HarnessRevision[]; users: HarnessUser[] } {
  const rows = ownerUserId ? revisions.filter((row) => row.owner_user_id === ownerUserId) : revisions;
  return {
    work: clone(work),
    revisions: clone([...rows].sort((a, b) => b.saved_at.localeCompare(a.saved_at))),
    users: clone(HARNESS_USERS),
  };
}

export function saveHarnessRevision(input: {
  owner_user_id: string;
  expected_latest_revision_id: string | null;
  source_revision_id: string | null;
  idempotency_key: string;
  document: HarnessDocument;
}): HarnessRevision {
  const user = HARNESS_USERS.find((candidate) => candidate.id === input.owner_user_id);
  if (!user) throw new Error("Unknown harness user");
  const duplicate = revisions.find((row) => row.owner_user_id === user.id && row.idempotency_key === input.idempotency_key);
  if (duplicate) return clone(duplicate);
  const own = revisions.filter((row) => row.owner_user_id === user.id).sort((a, b) => b.save_number - a.save_number);
  if ((own[0]?.id ?? null) !== input.expected_latest_revision_id) throw new Error("Your saved version changed. Reload before saving.");
  const source = input.source_revision_id ? revisions.find((row) => row.id === input.source_revision_id) : null;
  if (input.source_revision_id && !source) throw new Error("The selected source revision is no longer retained");
  clock += 5;
  const next = (own[0]?.save_number ?? 0) + 1;
  const row: HarnessRevision = {
    id: crypto.randomUUID(),
    work_id: work.id,
    owner_user_id: user.id,
    owner_name: user.name,
    save_number: next,
    saved_at: stamp(clock),
    device_name: user.device,
    surface: user.surface,
    idempotency_key: input.idempotency_key,
    source_revision_id: source?.id ?? null,
    source_owner_name: source?.owner_name ?? null,
    source_save_number: source?.save_number ?? null,
    document: clone(input.document),
  };
  revisions.push(row);
  return clone(row);
}

export function advanceHarnessWork(): HarnessWork {
  work = { ...work, stage: "job", job_ref: "J092610242" };
  return clone(work);
}
